import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";

function hotelHeader() {
  const c = typeof document !== "undefined"
    ? document.cookie.split(";").map((x) => x.trim()).find((x) => x.startsWith("active_hotel_id="))
    : null;
  const v = c ? decodeURIComponent(c.split("=")[1]) : null;
  return v ? { headers: { "X-Hocp-Hotel": v } } : {};
}
const H = hotelHeader;

export const docsAPI = {
  receipts: (params) => api.get("/receipts", { ...H(), params }).then((r) => r.data.items),
  eligiblePayments: (stayId) => api.get("/receipts/eligible-payments", { ...H(), params: { stay_id: stayId } }).then((r) => r.data.items),
  createReceipt: (body) => api.post("/receipts", body, H()).then((r) => r.data),
  quote: (body) => api.post("/receipts/quote", body, H()).then((r) => r.data),
  folioSummary: (stayId) => api.get("/bills/folio-summary", { ...H(), params: { stay_id: stayId } }).then((r) => r.data),
  taxConfig: () => api.get("/documents/tax-config", H()).then((r) => r.data),
  voidReceipt: (id, reason) => api.patch(`/receipts/${id}/void`, { reason }, H()).then((r) => r.data),
  receiptPdfUrl: (id) => `${API_BASE}/receipts/${id}/pdf`,

  bills: (params) => api.get("/bills", { ...H(), params }).then((r) => r.data.items),
  bill: (id) => api.get(`/bills/${id}`, H()).then((r) => r.data),
  createProvisional: (stayId) => api.post("/bills/provisional", { stay_id: stayId }, H()).then((r) => r.data),
  updateBill: (id, body) => api.patch(`/bills/${id}`, body, H()).then((r) => r.data),
  showBill: (id) => api.patch(`/bills/${id}/show`, {}, H()).then((r) => r.data),
  finaliseBill: (id) => api.post(`/bills/${id}/finalise`, {}, H()).then((r) => r.data),
  voidBill: (id, reason) => api.patch(`/bills/${id}/void`, { reason }, H()).then((r) => r.data),
  billPdfUrl: (id, category) => `${API_BASE}/bills/${id}/pdf${category ? `?category=${category}` : ""}`,
  setLineCategory: (stayId, ref, category) => api.patch("/bills/line-category", { stay_id: stayId, ref, category }, H()).then((r) => r.data),

  mergeGroup: (stayId) => api.get(`/merged-bills/group/${stayId}`, H()).then((r) => r.data),
  createMerged: (stayIds, scope) => api.post("/merged-bills", { stay_ids: stayIds, scope }, H()).then((r) => r.data),
  updateMerged: (id, body) => api.patch(`/merged-bills/${id}`, body, H()).then((r) => r.data),
  finaliseMerged: (id) => api.post(`/merged-bills/${id}/finalise`, {}, H()).then((r) => r.data),
  voidMerged: (id, reason) => api.patch(`/merged-bills/${id}/void`, { reason }, H()).then((r) => r.data),
  settleMerged: (id, body) => api.post(`/merged-bills/${id}/settle`, body, H()).then((r) => r.data),
  mergedPdfUrl: (id) => `${API_BASE}/merged-bills/${id}/pdf`,
};

export const docUrl = (type, id, fmt = "pdf") => `${API_BASE}/documents/${type}/${id}/${fmt}`;
/** REG-PRE-CHECKIN · registration card(s) rendered straight from a reservation (all rooms, or `?room=N`). */
export const reservationRegCardUrl = (rid, room) => `${API_BASE}/documents/registration_card/reservation/${rid}/pdf${room ? `?room=${room}` : ""}`;
export const voucherUrl = (rid) => docUrl("booking_voucher", rid);
export const stayDocuments = (stayId) => api.get(`/documents/stay/${stayId}`).then((r) => r.data.documents);

function filenameFromDisposition(header, fallback) {
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header || "");
  return m ? decodeURIComponent(m[1]) : fallback;
}

/** Fetches the PDF as a blob (cookies + X-Hocp-Hotel header) and saves it via <a download>. */
export async function downloadPdf(url, fallbackName = "document.pdf") {
  try {
    const path = url.startsWith(API_BASE) ? url.slice(API_BASE.length) : url;
    const res = await api.get(path, { responseType: "blob", params: { download: 1 } });
    const name = filenameFromDisposition(res.headers?.["content-disposition"], fallbackName);
    const href = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = href; a.download = name; a.rel = "noopener"; a.style.display = "none";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 4000);
    return name;
  } catch (e) {
    toast.error("Download failed. Please try again.");
    throw e;
  }
}

/** Opens the PDF inline in a new tab; if the popup is blocked, falls back to downloading it. */
export function openPdf(url, fallbackName) {
  let w = null;
  try { w = window.open(url, "_blank", "noopener"); } catch { w = null; }
  if (!w) downloadPdf(url, fallbackName).catch(() => {});
}

export const fmtINR = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Friendly filename prefix for a bill doc (mirrors the backend). */
export const billFileKind = (b) => (b?.bill_type === "provisional" ? "ProvisionalBill" : b?.is_gst_invoice ? "TaxInvoice" : "Invoice");

export const BILL_STATUS_TONE = { draft: "warning", shown_to_guest: "info", finalised: "success", void: "error", issued: "success", cancelled: "error" };

export const invoicesAPI = {
  list: (stayId) => api.get("/pms/invoices", { params: { stay_id: stayId } }).then((r) => r.data.items),
  preview: (body) => api.post("/pms/invoices/preview", body).then((r) => r.data),
  create: (body) => api.post("/pms/invoices", body).then((r) => r.data),
  cancel: (id, reason) => api.patch(`/pms/invoices/${id}/cancel`, { reason }).then((r) => r.data),
  pdfUrl: (id) => `${API_BASE}/pms/invoices/${id}/pdf`,
};

/** BILL-SPLIT · guest-facing bill categories (mirrors backend BILL_CATEGORIES). */
export const BILL_CATEGORIES = [["room", "Room Charges"], ["fnb", "Food & Beverage"], ["extras", "Extras"]];
export const BILL_CATEGORY_LABEL = Object.fromEntries(BILL_CATEGORIES);
export const BILL_CATEGORY_SHORT = { room: "Room", fnb: "F&B", extras: "Extras" };

/** Fetch the PDF with auth and hand it straight to the browser's print dialog (hidden iframe). Falls back to opening it. */
export async function printPdf(url, fallbackName = "document.pdf") {
  try {
    const res = await api.get(url.replace(API_BASE, ""), { ...H(), responseType: "blob" });
    const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const frame = document.createElement("iframe");
    frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    frame.src = blobUrl;
    document.body.appendChild(frame);
    frame.onload = () => { try { frame.contentWindow.focus(); frame.contentWindow.print(); } catch { window.open(blobUrl, "_blank"); } };
    setTimeout(() => { URL.revokeObjectURL(blobUrl); frame.remove(); }, 120000);
  } catch { openPdf(url, fallbackName); }
}
